import {
  CREATE,
  CreateParams,
  DELETE,
  DELETE_MANY,
  DeleteManyParams,
  DeleteParams,
  GET_LIST,
  GET_MANY,
  GET_MANY_REFERENCE,
  GET_ONE,
  GetListParams,
  GetManyParams,
  GetManyReferenceParams,
  GetOneParams,
  UPDATE,
  UPDATE_MANY,
  UpdateManyParams,
  UpdateParams,
} from "ra-core";

import {
  customRules,
  encodeObjectToQueryParams,
  formatListQueryParams,
} from "../../helpers/api/payloadFixer";

const homemadeClient = (
  host: string,
  httpClient: (
    url: string,
    options?: { method: string; body?: string },
  ) => Promise<{
    status: number;
    headers: Headers;
    body: string;
    json: Record<string, { docs: Record<string, unknown> }>;
  }>,
) => ({
  getList: async (resource: string, params: GetListParams) => {
    const query = formatListQueryParams(params);
    const { json } = await httpClient(
      `${host}/${resource}/paginated?${encodeObjectToQueryParams(query)}`,
    );

    const data = customRules(json.docs, GET_LIST);

    return {
      data,
      pageInfo: {
        hasPreviousPage: false,
        hasNextPage: true,
      },
    };
  },

  getOne: async (resource: string, params: GetOneParams) => {
    const { json } = await httpClient(`${host}/${resource}/${params.id}`);

    const data = customRules(json, GET_ONE);

    return { data };
  },

  getMany: async (resource: string, params: GetManyParams) => {
    const data = await Promise.all(
      params.ids.map(async (id) => {
        const { json } = await httpClient(`${host}/${resource}/${id}`);

        return customRules(json, GET_MANY);
      }),
    );

    return { data };
  },

  getManyReference: async (
    resource: string,
    params: GetManyReferenceParams,
  ) => {
    const query = formatListQueryParams(params);

    const { json } = await httpClient(
      `${host}/${resource}?${encodeObjectToQueryParams(query)}`,
    );

    const data = customRules(json, GET_MANY_REFERENCE);

    return { data, total: data.length };
  },

  update: async (resource: string, params: UpdateParams) => {
    const { json } = await httpClient(`${host}/${resource}/${params.id}`, {
      method: "PUT",
      body: JSON.stringify(params.data),
    });

    const data = customRules(json, UPDATE);

    return { data };
  },

  updateMany: async (resource: string, params: UpdateManyParams) => {
    const data = await Promise.all(
      params.ids.map(async (id) => {
        const { json } = await httpClient(`${host}/${resource}/${id}`, {
          method: "PUT",
          body: JSON.stringify(params.data),
        });

        return customRules(json, UPDATE_MANY);
      }),
    );

    return { data };
  },

  create: async (resource: string, params: CreateParams) => {
    const { json } = await httpClient(`${host}/${resource}`, {
      method: "POST",
      body: JSON.stringify(params.data),
    });

    const data = customRules(json, CREATE);

    return { data };
  },

  delete: async (resource: string, params: DeleteParams) => {
    const { json } = await httpClient(`${host}/${resource}/${params.id}`, {
      method: "DELETE",
    });

    const data = customRules(json, DELETE);

    return { data };
  },

  deleteMany: async (resource: string, params: DeleteManyParams) => {
    const data = await Promise.all(
      params.ids.map(async (id) => {
        const { json } = await httpClient(`${host}/${resource}/${id}`, {
          method: "DELETE",
        });

        return customRules(json, DELETE_MANY);
      }),
    );

    return { data };
  },
});

export default homemadeClient;
